import { Links, Meta, Outlet, Scripts, ScrollRestoration, useRouteError, useSearchParams } from "@remix-run/react";
import { captureRemixErrorBoundaryError } from "@sentry/remix";
import { MetaFunction, json } from "@vercel/remix";
import { cacheHeader } from "pretty-cache-header";

import { DialogDescription, DialogHeader, DialogTitle, Modal } from "./components";
import { ContactUsForm } from "./components/ContactUsForm";
import GlobalProgress from "./components/GlobalProgress";
import { DebugBreakpoints } from "./components/Tailwind";
import "./tailwind.css";

export const meta: MetaFunction = () => {
    return [{ title: "Our Guidant" }];
};

export async function loader() {
    const ENV = {};
    return json({ ENV }, { headers: { "Cache-Control": cacheHeader({ maxAge: "5 min" }) } });
}

export function Layout({ children }: { children: React.ReactNode }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const contactUsContext = searchParams.get("contactUs");
    const contactUsOpen = !!contactUsContext;
    const onOpenChange = (open: boolean) => {
        if (!open) {
            searchParams.delete("contactUs");
            setSearchParams(searchParams);
        }
    };

    const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

    return (
        <html lang="en">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Playfair:ital,opsz,wght@0,5..1200,300..900;1,5..1200,300..900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap"
                    rel="stylesheet"
                />
                <Meta />
                <Links />
                <script
                    dangerouslySetInnerHTML={{
                        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','GTM-5TVN3X9');`,
                    }}
                />
                <script
                    dangerouslySetInnerHTML={{
                        __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f); })(window,document,'script','dataLayer','GTM-T4GN69PL');`,
                    }}
                />
                <script async src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}></script>
                <script
                    dangerouslySetInnerHTML={{
                        __html: `
                            window.dataLayer = window.dataLayer || [];
                            function gtag(){dataLayer.push(arguments);}
                            window.gtag = gtag;
                            gtag('js', new Date());
                            gtag('config', '${GA_ID}');
                            `,
                    }}
                />
            </head>
            <body>
                <GlobalProgress />
                {children}
                <ScrollRestoration />
                <Scripts />

                <DebugBreakpoints />

                {contactUsOpen && (
                    <Modal open={contactUsOpen} onOpenChange={onOpenChange} triggerButton={false}>
                        <DialogHeader>
                            <DialogTitle>Contact Us</DialogTitle>
                            <DialogDescription>
                                <p>
                                    Currently, we only offer cremation packages for online purchase. If you are
                                    interested in funeral services or traditional burial, please contact one of our
                                    counselors using this form.
                                </p>
                            </DialogDescription>
                        </DialogHeader>

                        <ContactUsForm context={contactUsContext} />
                    </Modal>
                )}
            </body>
        </html>
    );
}

export const ErrorBoundary = () => {
    const error = useRouteError();
    console.error(error);
    captureRemixErrorBoundaryError(error);
    return <div>Something went wrong</div>;
};

export default function App() {
    return <Outlet />;
}
